import { useState, Fragment } from "react";
import { Alert, AlertDescription } from "@/ui/components/primitives/alert";
import { Button } from "@/ui/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/components/primitives/dialog";
import { Input } from "@/ui/components/primitives/input";
import { Label } from "@/ui/components/primitives/label";
import { AlertTriangle, Eye, EyeOff } from "lucide-react";
import { disableMFA } from "@/services/api/services/mfa";
import { showSuccessToast, showErrorToast } from "@/services/toast-helper";

interface MFADisableModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMFADisabled: () => void;
}

export function MFADisableModal({ open, onOpenChange, onMFADisabled }: MFADisableModalProps) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDisable = async () => {
    if (!password) {
      showErrorToast("Please enter your password");
      return;
    }

    setLoading(true);
    try {
      const response = await disableMFA(password);
      if (response.success) {
        showSuccessToast("MFA disabled successfully");
        setPassword("");
        setShowPassword(false);
        onMFADisabled();
        onOpenChange(false);
      }
    } catch (error: any) {
      showErrorToast("Failed to disable MFA: " + (error.message || "Invalid password"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPassword("");
    setShowPassword(false);
    onOpenChange(false);
  };

  return (
    <Fragment>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm w-full mx-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Disable Two-Factor Authentication
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to disable MFA? This will make your account less secure.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Warning:</strong> Disabling two-factor authentication removes an important
              security layer from your account. Your account will be protected only by your password.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="password">Confirm Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Enter your password to confirm disabling MFA
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDisable}
            disabled={loading || !password}
          >
            {loading ? "Disabling..." : "Disable MFA"}
          </Button>
        </DialogFooter>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
}